import { Alert, Box } from '@mui/material';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useDeviceAlerts } from '@/api/customSensorAlerts/useDeviceAlerts';
import { ConsolidatedWaveDataItem } from '@/api/historicalData';
import { useWaveData } from '@/api/historicalData/useWaveData';
import { usePreferredTimeZone } from '@/api/userAccount';
import { wavePeriodBulkIcon } from '@/assets';
import { Line, LineChart, LineDataSlice, drawLineChart } from '@/charts';
import { colorsets } from '@/charts/common';
import { addRenderersByProcessingSource } from '@/charts/helpers';
import { AlertLine } from '@/charts/types';
import { SensorIcon } from '@/components/data/SensorIcon';
import { ChartLineSelector, ChartLineSelectorProps } from '@/components/spotter/historicalDataTab/ChartLineSelector';
import {
    ChartDataDetails,
    ChartDataSelectors,
    ChartWithInfo,
    ProcessingSourceLegendItems,
} from '@/components/spotter/historicalDataTab/ChartWithInfo';
import { DataDetailsByTimestamp } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import {
    DataLineExtent,
    DataLineMean,
    LineChartDataSummary,
} from '@/components/spotter/historicalDataTab/datumDetail/LineChartDataSummary';
import { LineChartDatumDetail } from '@/components/spotter/historicalDataTab/datumDetail/LineChartDatumDetail';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useElementSize } from '@/hooks/useElementSize';
import { useTrackChartView } from '@/hooks/useIsViewedTracking';
import { useGlobalStore } from '@/store';
import { composeAlertLines } from '@/utils';

export type LineConfig = Line<ConsolidatedWaveDataItem>;

export const MEAN_BULK_WAVE_PERIOD_LINE = {
    id: 'wave-mean-period',
    label: 'Mean period',
    sensorLabel: 'Bulk',
    selectorLabel: 'Bulk mean',
    valueKey: 'meanPeriod',
    unit: 's',
    unitType: 'time',
    color: colorsets.blue,
} satisfies LineConfig & ChartLineSelectorProps['lines'][number];

export const PEAK_BULK_WAVE_PERIOD_LINE = {
    id: 'wave-peak-period',
    label: 'Peak period',
    sensorLabel: 'Bulk',
    selectorLabel: 'Bulk peak',
    valueKey: 'peakPeriod',
    unit: 's',
    unitType: 'time',
    color: colorsets.yellow,
} satisfies LineConfig & ChartLineSelectorProps['lines'][number];

const BULK_WAVE_PERIOD_LINES = [MEAN_BULK_WAVE_PERIOD_LINE, PEAK_BULK_WAVE_PERIOD_LINE];

const PARTITION_WAVE_PERIOD_LINES = [
    {
        id: 'wave-mean-period-swell',
        sensorLabel: 'Swell',
        label: 'Mean period',
        valueKey: 'meanPeriodSwell',
        unit: 's',
        unitType: 'time',
        color: colorsets.green,
    },
    {
        id: 'wave-mean-period-sea',
        label: 'Mean period',
        sensorLabel: 'Sea',
        valueKey: 'meanPeriodSea',
        unit: 's',
        unitType: 'time',
        color: colorsets.red,
    },
] satisfies (LineConfig & ChartLineSelectorProps['lines'][number])[];

export type WavePeriodChartProps = {
    chartTitle: string;
    spotterId: string;
    queryEnabled?: boolean;
    showAlertLines: boolean;
    scaleToFitAlerts: boolean;
    alertLines?: AlertLine[];
    lines: LineConfig[];
    onSelectData?: (data: LineDataSlice<ConsolidatedWaveDataItem, LineConfig> | null) => void;
    onSelectTimestamp?: (timestamp: string | null, timeline?: string[]) => void;
};

export const WavePeriodChart = ({
    chartTitle,
    spotterId,
    queryEnabled = true,
    showAlertLines,
    scaleToFitAlerts,
    alertLines,
    onSelectData,
    onSelectTimestamp,
    lines,
}: WavePeriodChartProps) => {
    const id = useId();
    const timeZone = usePreferredTimeZone();
    const { dateRange } = useDateRangeContext();
    const chartRef = useRef<LineChart<ConsolidatedWaveDataItem, LineConfig> | null>(null);
    const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
    const { selectedTimestamp } = useTimelineNavigationContext();
    const selectedTimestampRef = useRef(selectedTimestamp);
    selectedTimestampRef.current = selectedTimestamp;

    useTrackChartView({
        containerEl,
        spotterId,
        chartTitle,
    });

    const { data: waveData, isPending } = useWaveData({
        dateRange,
        config: { enabled: queryEnabled },
    });

    const containerRef = useRef<HTMLElement>(null);
    const { width, height } = useElementSize(containerRef);

    const containerId = `wave-period-chart${id}`;
    const drawChart = useCallback(drawLineChart, []);
    const linesWithRenderers = useMemo(() => addRenderersByProcessingSource(lines, waveData), [lines, waveData]);

    useEffect(() => {
        if (isPending) {
            return;
        }
        if (!waveData) {
            return;
        }

        const newChart = drawChart<ConsolidatedWaveDataItem>({
            dataset: waveData,
            lines: linesWithRenderers,
            xAxisKey: 'timestamp',
            yAxisLabel: 'Wave period',
            containerId,
            showAlertLines: showAlertLines,
            scaleChartToFitAllAlerts: scaleToFitAlerts,
            alertLines,
            chartWidth: width,
            chartHeight: height,
            dateRange,
            timeZone,
            onSelectData,
            onSelectTimestamp,
        });
        chartRef.current = newChart;
        newChart.setSelectedTimestamp?.(selectedTimestampRef.current ? new Date(selectedTimestampRef.current) : null);

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [
        alertLines,
        containerId,
        dateRange,
        timeZone,
        drawChart,
        height,
        isPending,
        linesWithRenderers,
        onSelectData,
        onSelectTimestamp,
        scaleToFitAlerts,
        showAlertLines,
        waveData,
        width,
    ]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp?.(selectedTimestamp ? new Date(selectedTimestamp) : null);
    }, [selectedTimestamp]);

    useLayoutEffect(() => {
        if (!containerRef.current) {
            return;
        }
        setContainerEl(containerRef.current);
    }, [waveData?.length, dateRange]);

    return <Box ref={containerRef} id={containerId} width={1} height={1} />;
};

export const WavePeriodSection = () => {
    const { setSelectedTimestamp } = useTimelineNavigationContext();
    const chartTitle = 'Wave period';
    const { spotterId } = useSpotterContext();
    const [activeLinesId, setActiveLinesId] = useState(
        [...BULK_WAVE_PERIOD_LINES, ...PARTITION_WAVE_PERIOD_LINES].map((line) => line.id)
    );
    const { dateRange } = useDateRangeContext();
    const isAlertLinesOn = useGlobalStore((state) => state.isAlertLinesOn);
    const isScaleToFitAlertLinesOn = useGlobalStore((state) => state.isScaleToFitAlertLinesOn);
    const [selectedData, setSelectedData] = useState<LineDataSlice<ConsolidatedWaveDataItem> | null>(null);

    const {
        data: waveData,
        isPending,
        isFetching,
        hasPartitionData,
    } = useWaveData({
        dateRange,
    });
    const { data: deviceAlerts } = useDeviceAlerts({
        config: { enabled: isAlertLinesOn },
    });

    const alertLines = useMemo(
        () => [
            ...composeAlertLines({
                deviceAlerts,
                triggerTypes: ['spotter_threshold_peakwaveperiod'],
            }).map((alert) => ({ ...alert, label: 'Peak' })),
            ...composeAlertLines({
                deviceAlerts,
                triggerTypes: ['spotter_threshold_meanwaveperiod'],
            }).map((alert) => ({ ...alert, label: 'Mean' })),
        ],
        [deviceAlerts]
    );

    const lines = useMemo(
        () => (hasPartitionData ? [...BULK_WAVE_PERIOD_LINES, ...PARTITION_WAVE_PERIOD_LINES] : BULK_WAVE_PERIOD_LINES),
        [hasPartitionData]
    );

    const activeLines = useMemo(() => lines.filter((line) => activeLinesId.includes(line.id)), [activeLinesId, lines]);

    return (
        <CollapsibleSection
            isFetching={isFetching}
            isPending={isPending}
            title={chartTitle}
            icon={<SensorIcon src={wavePeriodBulkIcon} />}
        >
            {waveData?.length ? (
                <Box>
                    <ChartWithInfo
                        legend={<ProcessingSourceLegendItems data={waveData} />}
                        chart={
                            <WavePeriodChart
                                onSelectTimestamp={setSelectedTimestamp}
                                lines={activeLines}
                                spotterId={spotterId!}
                                chartTitle={chartTitle}
                                onSelectData={setSelectedData}
                                showAlertLines={isAlertLinesOn}
                                scaleToFitAlerts={isScaleToFitAlertLinesOn}
                                alertLines={alertLines}
                            />
                        }
                    >
                        <ChartDataSelectors>
                            <ChartLineSelector lines={lines} allLinesLabel="All data" onChange={setActiveLinesId} />
                        </ChartDataSelectors>
                        <ChartDataDetails>
                            {!selectedData && (
                                <LineChartDataSummary
                                    dateRange={dateRange}
                                    lines={activeLines}
                                    dataset={waveData}
                                    renderLineSummary={() => (
                                        <>
                                            <DataLineMean />
                                            <DataLineExtent />
                                        </>
                                    )}
                                />
                            )}
                            <DataDetailsByTimestamp
                                selectedData={selectedData}
                                datumRenderer={({ lines, datum }) => (
                                    <LineChartDatumDetail dataset={waveData} datum={datum} lines={lines} />
                                )}
                            />
                        </ChartDataDetails>
                    </ChartWithInfo>
                </Box>
            ) : (
                <Alert severity="info">No wave period data for selected date range</Alert>
            )}
        </CollapsibleSection>
    );
};
